import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, CheckCircle, AlertCircle, Lock, HelpCircle, Download, Database, Sun, Zap, Palette } from 'lucide-react';
import api from '../../services/api';
import PageHeader from '../../components/PageHeader';
import { crumbsFor } from '../../navigation/modules';
import { useTheme } from '../../contexts/ThemeContext';
import { isHexColor, brandVars } from '../../utils/color';
import { useTabParam } from '../../hooks/useTabParam';

const AdminConfig = () => {
    const [activeTab, setActiveTab] = useTabParam(['general', 'appearance', 'saml'], 'general');
    const [config, setConfig] = useState({
        orgName: '',
        orgLogoUrl: '',
        authMode: 'WHITELIST',
        allowedDomains: '',
        samlEntryPoint: '',
        samlIssuer: '',
        samlCert: '',
        baseCurrency: 'INR',
        themeDefault: 'turquoise',
        brandColor: '',
        companyLegalName: '',
        companyAddress: '',
        companyPan: '',
        companyTan: '',
        companyCin: '',
        companyPfReg: '',
        companyEsiReg: '',
    });

    const [showSamlHelp, setShowSamlHelp] = useState(false);
    const [isConfigured, setIsConfigured] = useState({ saml: false });
    const { theme, previewBrand, previewPreset, refreshOrgTheme } = useTheme();
    // Leaving the page drops any unsaved previews.
    useEffect(() => () => { previewBrand(null); previewPreset(null); }, [previewBrand, previewPreset]);
    const [savedBrand, setSavedBrand] = useState(''); // as loaded, to send the colour only when changed

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        try {
            const response = await api.get('/admin/config');
            setConfig(prev => ({
                ...prev,
                orgName: response.data.orgName || '',
                orgLogoUrl: response.data.orgLogoUrl || '',
                authMode: response.data.authMode || 'WHITELIST',
                allowedDomains: response.data.allowedDomains || '',
                samlEntryPoint: response.data.samlEntryPoint || '',
                samlIssuer: response.data.samlIssuer || '',
                samlCert: '', // Don't show secrets
                baseCurrency: response.data.baseCurrency || 'INR',
                themeDefault: response.data.themeDefault || 'turquoise',
                brandColor: response.data.brandColor || '',
                companyLegalName: response.data.companyLegalName || '',
                companyAddress: response.data.companyAddress || '',
                companyPan: response.data.companyPan || '',
                companyTan: response.data.companyTan || '',
                companyCin: response.data.companyCin || '',
                companyPfReg: response.data.companyPfReg || '',
                companyEsiReg: response.data.companyEsiReg || '',
            }));
            setSavedBrand(response.data.brandColor || '');
            setIsConfigured({ saml: response.data.samlAuthConfigured });
        } catch (error) {
            console.error('Failed to fetch config', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setMessage(null);
        // The brand colour is shared with the platform owner's editor: send it
        // only when it was changed here, and catch a bad value before saving.
        const { brandColor, ...rest } = config;
        const brandChanged = brandColor !== savedBrand;
        if (brandChanged && brandColor !== '' && !isHexColor(brandColor)) {
            setActiveTab('appearance');
            setMessage({ type: 'error', text: 'Brand colour must look like #7c3aed (or be empty). Nothing was saved.' });
            return;
        }
        setSaving(true);
        try {
            await api.post('/admin/config', brandChanged ? config : rest);
            setMessage({ type: 'success', text: 'Configuration saved successfully!' });
            fetchConfig();
            // Everyone's look, incl. what was just saved. If that refresh fails,
            // keep showing the previews (they match what was saved).
            if (await refreshOrgTheme()) { previewBrand(null); previewPreset(null); }
        } catch (error) {
            console.error('Failed to save config', error);
            setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to save configuration.' });
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (field, value) => {
        setConfig(prev => ({ ...prev, [field]: value }));
    };

    const handleMetadataUpload = (file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const parser = new DOMParser();
                const xml = parser.parseFromString(e.target.result, 'text/xml');

                // Helper: find elements by local name regardless of namespace prefix
                const findByLocalName = (localName) => Array.from(xml.getElementsByTagName('*'))
                    .filter(el => el.localName === localName);

                // Extract SSO URL — prefer HTTP-Redirect binding
                const ssoEls = findByLocalName('SingleSignOnService');
                let ssoUrl = '';
                for (let el of ssoEls) {
                    if ((el.getAttribute('Binding') || '').includes('HTTP-Redirect')) {
                        ssoUrl = el.getAttribute('Location') || '';
                        break;
                    }
                }
                if (!ssoUrl && ssoEls[0]) ssoUrl = ssoEls[0].getAttribute('Location') || '';

                // Extract Certificate
                const certEls = findByLocalName('X509Certificate');
                const cert = certEls[0] ? certEls[0].textContent.replace(/\s+/g, '') : '';

                console.log('[SAML Metadata] SSO URL:', ssoUrl);
                console.log('[SAML Metadata] Cert length:', cert.length);

                if (!ssoUrl && !cert) {
                    setMessage({ type: 'error', text: '⚠️ Could not find SSO URL or Certificate in the XML. Make sure you uploaded the correct IdP metadata file.' });
                    return;
                }

                setConfig(prev => ({
                    ...prev,
                    samlEntryPoint: ssoUrl || prev.samlEntryPoint,
                    samlCert: cert || prev.samlCert,
                }));
                setMessage({ type: 'success', text: `✅ Metadata parsed! SSO URL and Certificate filled automatically. Review the fields below and click Save.` });
            } catch (err) {
                console.error('Metadata parse error:', err);
                setMessage({ type: 'error', text: 'Failed to parse metadata XML: ' + err.message });
            }
        };
        reader.readAsText(file);
    };


    if (loading) return <div className="p-6">Loading configuration...</div>;

    const TabButton = ({ id, label, icon: Icon }) => (
        <button
            onClick={() => setActiveTab(id)}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '1rem 1.5rem',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                borderBottom: activeTab === id ? '2px solid var(--primary)' : 'none',
                color: activeTab === id ? 'var(--primary)' : 'var(--text-muted)',
                fontWeight: activeTab === id ? 600 : 400,
                transition: 'all 0.2s'
            }}
        >
            {Icon && <Icon size={18} />}
            {label}
        </button>
    );

    return (
        <div className="container section">
            <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                <PageHeader crumbs={crumbsFor('orgConfig')} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                    <h1 className="gradient-text" style={{ fontSize: '2rem', margin: 0 }}>Organization Setup</h1>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <button onClick={() => navigate('/admin/logs')} className="btn btn-secondary">System Logs</button>
                        <button onClick={handleSave} disabled={saving} className="btn btn-primary">
                            <Save size={20} style={{ marginRight: '0.5rem' }} />
                            {saving ? 'Saving...' : 'Save All Changes'}
                        </button>
                    </div>
                </div>

                {message && (
                    <div className={`p-4 rounded-lg mb-6 flex items-center ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {message.type === 'success' ? <CheckCircle size={20} className="mr-2" /> : <AlertCircle size={20} className="mr-2" />}
                        {message.text}
                    </div>
                )}

                <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                        <TabButton id="general" label="General" icon={Lock} />
                        <TabButton id="appearance" label="Appearance" icon={Palette} />
                        <TabButton id="saml" label="SAML SSO" />
                    </div>

                    <div style={{ padding: '2rem' }}>
                        {activeTab === 'general' && (
                            <div style={{ display: 'grid', gap: '1.5rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Organization Name</label>
                                    <input
                                        type="text"
                                        value={config.orgName}
                                        onChange={(e) => handleChange('orgName', e.target.value)}
                                        className="form-input"
                                        placeholder="e.g. My Company Inc."
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Organization Logo URL</label>
                                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                                        <input
                                            type="text"
                                            value={config.orgLogoUrl}
                                            onChange={(e) => handleChange('orgLogoUrl', e.target.value)}
                                            className="form-input"
                                            placeholder="https://example.com/logo.png"
                                            style={{ flex: 1 }}
                                        />
                                        {config.orgLogoUrl && (
                                            <img src={config.orgLogoUrl} alt="Preview" style={{ height: '40px', objectFit: 'contain', border: '1px solid var(--border)', borderRadius: '4px' }} />
                                        )}
                                    </div>
                                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>Visible on login page and navigation.</p>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Registration Policy</label>
                                    <select
                                        value={config.authMode}
                                        onChange={(e) => handleChange('authMode', e.target.value)}
                                        className="form-input"
                                    >
                                        <option value="WHITELIST">Controlled — Only allow pre-added users (Recommended)</option>
                                        <option value="AUTO_CREATE">Self-service — Auto-create any SSO user</option>
                                    </select>
                                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                                        {config.authMode === 'WHITELIST'
                                            ? '✅ Only users added by HR/Admin can log in. SSO will reject unknown accounts.'
                                            : '⚠️ Any user who passes SSO will be auto-created. Use with caution.'}
                                    </p>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Allowed Email Domains <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
                                    <input
                                        type="text"
                                        value={config.allowedDomains}
                                        onChange={(e) => handleChange('allowedDomains', e.target.value)}
                                        className="form-input"
                                        placeholder="e.g. pathtoip.com, mycompany.org"
                                    />
                                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>Comma-separated. Blocks logins from outside these domains. Leave blank to allow any domain.</p>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Base Currency (Global)</label>
                                    <select
                                        value={config.baseCurrency}
                                        onChange={(e) => handleChange('baseCurrency', e.target.value)}
                                        className="form-input"
                                    >
                                        <option value="INR">INR (₹)</option>
                                        <option value="USD">USD ($)</option>
                                        <option value="EUR">EUR (€)</option>
                                        <option value="GBP">GBP (£)</option>
                                        <option value="JPY">JPY (¥)</option>
                                        <option value="AUD">AUD (A$)</option>
                                        <option value="CAD">CAD (C$)</option>
                                    </select>
                                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                                        Used for Payroll and default conversion in Expense Reports.
                                    </p>
                                </div>

                                <div style={{ marginTop: '1rem', padding: '1.5rem', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                                    <h3 style={{ fontSize: '1.1rem', marginBottom: '0.25rem' }}>Company details (salary slips)</h3>
                                    <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                                        Printed on every employee's salary slip, along with the organisation logo above. Leave blank to omit.
                                    </p>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
                                        <div style={{ gridColumn: 'auto' }}>
                                            <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.9rem' }}>Registered company name</label>
                                            <input type="text" value={config.companyLegalName} onChange={(e) => handleChange('companyLegalName', e.target.value)} className="form-input" placeholder="e.g. Acme Technologies Private Limited" />
                                        </div>
                                        <div style={{ gridColumn: '1 / -1' }}>
                                            <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.9rem' }}>Registered address</label>
                                            <textarea rows="2" value={config.companyAddress} onChange={(e) => handleChange('companyAddress', e.target.value)} className="form-input" placeholder="Street, City, State, PIN" />
                                        </div>
                                        <div style={{ gridColumn: 'auto' }}>
                                            <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.9rem' }}>Company PAN</label>
                                            <input type="text" value={config.companyPan} onChange={(e) => handleChange('companyPan', e.target.value)} className="form-input" placeholder="AAACA1234A" />
                                        </div>
                                        <div style={{ gridColumn: 'auto' }}>
                                            <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.9rem' }}>TAN</label>
                                            <input type="text" value={config.companyTan} onChange={(e) => handleChange('companyTan', e.target.value)} className="form-input" placeholder="BLRA12345B" />
                                        </div>
                                        <div style={{ gridColumn: 'auto' }}>
                                            <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.9rem' }}>CIN</label>
                                            <input type="text" value={config.companyCin} onChange={(e) => handleChange('companyCin', e.target.value)} className="form-input" placeholder="U72200KA2020PTC123456" />
                                        </div>
                                        <div style={{ gridColumn: 'auto' }}>
                                            <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.9rem' }}>PF registration no.</label>
                                            <input type="text" value={config.companyPfReg} onChange={(e) => handleChange('companyPfReg', e.target.value)} className="form-input" placeholder="KN/BNG/1234567" />
                                        </div>
                                        <div style={{ gridColumn: 'auto' }}>
                                            <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.9rem' }}>ESI registration no.</label>
                                            <input type="text" value={config.companyEsiReg} onChange={(e) => handleChange('companyEsiReg', e.target.value)} className="form-input" placeholder="53000123450001001" />
                                        </div>
                                    </div>
                                </div>

                                <div style={{ marginTop: '1rem', padding: '1.5rem', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                                    <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <Database size={20} className="text-gray-600" />
                                        Backup & Maintenance
                                    </h3>
                                    <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
                                        Download a complete dump of the database in JSON format. This include users, expenses, leaves, and system configurations. Use this for quick off-site backups.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => window.open(`${api.defaults.baseURL}/admin/db-dump`, '_blank')}
                                        className="btn btn-secondary"
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                                    >
                                        <Download size={18} />
                                        Download Database Backup (JSON)
                                    </button>
                                </div>
                            </div>
                        )}

                        {activeTab === 'appearance' && (
                            <div style={{ display: 'grid', gap: '2rem' }}>
                                <div>
                                    <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>Default Theme for this Organisation</h3>
                                    <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
                                        This is the theme new users see when they first log in. Each user can override it from the navbar toggle.
                                    </p>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                        {/* Peach card */}
                                        <button
                                            type="button"
                                            onClick={() => { handleChange('themeDefault', 'peach'); previewPreset('peach'); }}
                                            style={{
                                                padding: '1.5rem',
                                                borderRadius: 'var(--radius)',
                                                border: `2px solid ${config.themeDefault === 'peach' ? '#f4845f' : '#fde8d8'}`,
                                                background: config.themeDefault === 'peach' ? 'rgba(244,132,95,0.06)' : '#fff8f3',
                                                cursor: 'pointer',
                                                textAlign: 'left',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                                                <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #fff8f3, #fef0e6)', border: '2px solid #f4845f', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <Sun size={18} color="#f4845f" />
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 700, color: '#1c1917', fontSize: '1rem' }}>Peach</div>
                                                    <div style={{ fontSize: '0.75rem', color: '#78716c' }}>Soft · Warm · Minimal</div>
                                                </div>
                                                {config.themeDefault === 'peach' && (
                                                    <CheckCircle size={18} color="#f4845f" style={{ marginLeft: 'auto' }} />
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', gap: '0.4rem' }}>
                                                {['#fff8f3', '#fef0e6', '#f4845f', '#e8683d', '#1c1917'].map(c => (
                                                    <div key={c} style={{ width: 20, height: 20, borderRadius: '50%', background: c, border: '1px solid rgba(0,0,0,0.08)' }} />
                                                ))}
                                            </div>
                                        </button>

                                        {/* Turquoise card */}
                                        <button
                                            type="button"
                                            onClick={() => { handleChange('themeDefault', 'turquoise'); previewPreset('turquoise'); }}
                                            style={{
                                                padding: '1.5rem',
                                                borderRadius: 'var(--radius)',
                                                border: `2px solid ${config.themeDefault === 'turquoise' ? '#2dd4bf' : '#334155'}`,
                                                background: config.themeDefault === 'turquoise' ? 'rgba(45,212,191,0.06)' : '#0f172a',
                                                cursor: 'pointer',
                                                textAlign: 'left',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                                                <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #0f172a, #1e293b)', border: '2px solid #2dd4bf', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <Zap size={18} color="#2dd4bf" />
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 700, color: '#f1f5f9', fontSize: '1rem' }}>Turquoise</div>
                                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Bold · Dark · Modern</div>
                                                </div>
                                                {config.themeDefault === 'turquoise' && (
                                                    <CheckCircle size={18} color="#2dd4bf" style={{ marginLeft: 'auto' }} />
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', gap: '0.4rem' }}>
                                                {['#0f172a', '#1e293b', '#2dd4bf', '#14b8a6', '#f1f5f9'].map(c => (
                                                    <div key={c} style={{ width: 20, height: 20, borderRadius: '50%', background: c, border: '1px solid rgba(255,255,255,0.12)' }} />
                                                ))}
                                            </div>
                                        </button>
                                    </div>
                                    <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '1rem' }}>
                                        Clicking a theme previews it instantly. Save All Changes to make it the default for your organisation.
                                    </p>
                                </div>

                                <div>
                                    <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>Brand colour</h3>
                                    <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                                        Your organisation's colour for buttons, links and highlights, in both themes. It is also used on salary slips and letters. Users can't change it.
                                    </p>
                                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                        <input aria-label="Brand colour picker" type="color" value={isHexColor(config.brandColor) ? config.brandColor : '#2dd4bf'}
                                            onChange={(e) => { handleChange('brandColor', e.target.value); previewBrand(e.target.value); }}
                                            style={{ width: 48, height: 40, border: '1px solid var(--border)', borderRadius: 8, background: 'transparent', padding: 2, cursor: 'pointer' }} />
                                        <input aria-label="Brand colour" value={config.brandColor} placeholder="#7c3aed (empty = theme colour)" maxLength={7}
                                            onChange={(e) => { const v = e.target.value.trim(); handleChange('brandColor', v); if (v === '' || isHexColor(v)) previewBrand(v); }}
                                            style={{ padding: '0.6rem 0.75rem', borderRadius: '0.5rem', border: `1px solid ${config.brandColor && !isHexColor(config.brandColor) ? '#ef4444' : 'var(--input-border, var(--border))'}`, background: 'var(--input-bg, var(--surface))', color: 'var(--text-main)', width: 220, fontFamily: 'monospace' }} />
                                        {config.brandColor && (
                                            <button type="button" onClick={() => { handleChange('brandColor', ''); previewBrand(''); }}
                                                style={{ padding: '0.5rem 0.9rem', borderRadius: '0.5rem', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-main)', cursor: 'pointer' }}>
                                                Reset to theme colour
                                            </button>
                                        )}
                                        <button type="button" className="btn btn-primary" style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }} tabIndex={-1}>Sample button</button>
                                    </div>
                                    {config.brandColor && !isHexColor(config.brandColor) && (
                                        <p style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.5rem' }}>Use a 6-digit hex colour like #7c3aed.</p>
                                    )}
                                    {isHexColor(config.brandColor) && (() => {
                                        const v = brandVars(config.brandColor, theme);
                                        const adjusted = v['--primary'] !== config.brandColor.toLowerCase();
                                        return (
                                            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                                                Button text: {v['--on-primary'] === '#ffffff' ? 'white' : 'dark'} (chosen automatically for readability).
                                                {adjusted && ` In this theme the colour is shown as ${v['--primary']} so it stands out from the background.`}
                                            </p>
                                        );
                                    })()}
                                </div>
                            </div>
                        )}

                        {activeTab === 'saml' && (
                            <div style={{ display: 'grid', gap: '1.5rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h3 style={{ margin: 0 }}>SAML SSO (Google Workspace, Okta, etc.)</h3>
                                    <button
                                        onClick={() => setShowSamlHelp(!showSamlHelp)}
                                        className="btn btn-secondary"
                                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}
                                    >
                                        <HelpCircle size={16} />
                                        {showSamlHelp ? 'Hide Setup Guide' : 'Show Setup Guide'}
                                    </button>
                                </div>

                                {/* Metadata XML Upload */}
                                <div style={{
                                    border: '2px dashed #c7d2fe',
                                    borderRadius: 'var(--radius)',
                                    padding: '1.5rem',
                                    background: '#f5f3ff',
                                    textAlign: 'center'
                                }}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => { e.preventDefault(); handleMetadataUpload(e.dataTransfer.files[0]); }}
                                >
                                    <Lock size={24} style={{ color: '#6366f1', marginBottom: '0.5rem' }} />
                                    <p style={{ fontWeight: 600, margin: '0 0 0.25rem' }}>Upload IdP Metadata XML <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(fastest setup)</span></p>
                                    <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0 0 0.75rem' }}>Download from Google Admin Console → your SAML app → Download metadata. <br />Drag & drop here or click to browse.</p>
                                    <label style={{ cursor: 'pointer' }}>
                                        <input
                                            type="file"
                                            accept=".xml,text/xml,application/xml"
                                            style={{ display: 'none' }}
                                            onChange={(e) => handleMetadataUpload(e.target.files[0])}
                                        />
                                        <span className="btn btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                                            📂 Browse for XML file
                                        </span>
                                    </label>
                                </div>

                                {showSamlHelp && (
                                    <div style={{
                                        background: 'var(--bg-secondary)',
                                        border: '1px solid var(--border)',
                                        borderRadius: 'var(--radius)',
                                        padding: '1.5rem',
                                        fontSize: '0.9rem',
                                        lineHeight: 1.6,
                                        color: '#334155'
                                    }}>
                                        <h4 style={{ marginTop: 0, color: 'var(--text-main)' }}>Google Workspace Setup</h4>
                                        <ol style={{ paddingLeft: '1.2rem', marginBottom: '1.5rem' }}>
                                            <li>Go to <a href="https://admin.google.com/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>Google Admin Console</a> &gt; <strong>Apps</strong> &gt; <strong>Web and mobile apps</strong>.</li>
                                            <li><strong>Add App</strong> &gt; <strong>Add custom SAML app</strong>.</li>
                                            <li><strong>Copy</strong> the <strong>SSO URL</strong>, <strong>Entity ID</strong>, and <strong>Download Certificate</strong>. Paste them below.</li>
                                            <li>Continue and name the app "PathToIP".</li>
                                            <li><strong>Service Provider Details:</strong>
                                                <ul style={{ listStyle: 'disc', marginLeft: '1rem', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                                                    <li><strong>ACS URL:</strong> <code>{window.location.origin}/api/auth/saml/callback</code></li>
                                                    <li><strong>Entity ID:</strong> <code>{window.location.origin}</code></li>
                                                    <li><strong>Name ID format:</strong> EMAIL</li>
                                                </ul>
                                            </li>
                                            <li>Finish and ensure user access is turned <strong>ON</strong>.</li>
                                        </ol>
                                        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                                            Map <strong>Primary Email</strong> → <code>email</code>, <strong>First Name</strong> → <code>firstName</code>, <strong>Last Name</strong> → <code>lastName</code>.
                                        </p>
                                    </div>
                                )}

                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: '#f0f9ff', padding: '1rem', borderRadius: 'var(--radius)', border: '1px solid #bae6fd' }}>
                                    <AlertCircle className="text-blue-600" size={24} />
                                    <div>
                                        <p style={{ fontWeight: 600, margin: 0 }}>Service Provider Info (For your IdP)</p>
                                        <p style={{ fontSize: '0.85rem', margin: 0 }}><strong>ACS URL:</strong> {window.location.origin}/api/auth/saml/callback</p>
                                        <p style={{ fontSize: '0.85rem', margin: 0 }}><strong>Entity ID:</strong> {window.location.origin}</p>
                                    </div>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>SSO URL (Entry Point) — <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>Google's login URL</span></label>
                                    <textarea
                                        rows={3}
                                        value={config.samlEntryPoint}
                                        onChange={(e) => handleChange('samlEntryPoint', e.target.value)}
                                        className="form-input"
                                        style={{ fontFamily: 'monospace', fontSize: '0.85rem', resize: 'vertical' }}
                                        placeholder="e.g. https://accounts.google.com/o/saml2/idp?idpid=..."
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Entity ID (Issuer) — <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>Must exactly match what you entered in Google Admin Console → SP Details</span></label>
                                    <textarea
                                        rows={2}
                                        value={config.samlIssuer}
                                        onChange={(e) => handleChange('samlIssuer', e.target.value)}
                                        className="form-input"
                                        style={{ fontFamily: 'monospace', fontSize: '0.85rem', resize: 'vertical' }}
                                        placeholder={`e.g. https://pathtoiptoolslab-git-827131241084.asia-south1.run.app/saml`}
                                    />
                                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>⚠️ This must be identical in both Google and here — it's how Google recognises your app.</p>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Certificate (X.509)</label>
                                    <textarea
                                        rows={10}
                                        value={config.samlCert}
                                        onChange={(e) => handleChange('samlCert', e.target.value)}
                                        className="form-input"
                                        style={{ fontFamily: 'monospace', fontSize: '0.8rem', resize: 'vertical' }}
                                        placeholder={isConfigured.saml ? "•••••••• (Paste new certificate to update)" : "-----BEGIN CERTIFICATE-----\nMIID...\n-----END CERTIFICATE-----"}
                                    />
                                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>Paste the entire contents of the certificate file downloaded from Google Admin Console.</p>
                                </div>

                                {isConfigured.saml && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#16a34a', fontWeight: 600 }}>
                                        <CheckCircle size={18} />
                                        SAML SSO is configured. Users will see a "Login with SSO" button on the login page.
                                    </div>
                                )}
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminConfig;
